import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Plus, AlertTriangle, Pencil, Trash2, ArrowUpCircle } from "lucide-react";
import { PageContainer } from "@/layout/PageContainer";
import { PageHeader } from "@/ui/PageHeader";
import { KPICard } from "@/ui/KPICard";
import { Card, CardBody, SectionHeading } from "@/ui/Card";
import { Badge } from "@/ui/Badge";
import { Avatar } from "@/ui/Avatar";
import { Button } from "@/ui/Button";
import { ProgressBar } from "@/ui/ProgressBar";
import { ConfirmDialog } from "@/ui/modals/ConfirmDialog";
import { Modal, Field, fieldInputClass } from "@/ui/modals/Modal";
import { LoadingState, EmptyState } from "@/ui/feedback/states";
import { ApiFallbackBanner } from "@/ui/feedback/ApiFallbackBanner";
import { toast } from "@/stores/toast.store";
import { getErrorMessage } from "@/lib/api-errors";
import { formatUSD, formatDZD, usdToDzd } from "@/lib/formatters";
import { WALLET_ALERT_THRESHOLD_USD, DEFAULT_EXCHANGE_RATE } from "@/lib/constants";
import type { Wallet } from "@/types";
import { useAuthStore } from "@/features/auth/auth.store";
import { useWallets, useTopUpWallet } from "./wallets.hooks";
import { useWalletsStore } from "./wallets.store";
import { useWalletTransactions } from "./wallet-transactions.hooks";

export function WalletsPage() {
  const { t } = useTranslation();
  const { wallets, isLoading, isError, refetch } = useWallets();
  const { topups } = useWalletTransactions();
  const topUp = useTopUpWallet();
  const authed = useAuthStore((s) => s.status === "authenticated");
  const archiveMock = useWalletsStore((s) => s.archive);
  const [topupFor, setTopupFor] = useState<Wallet | undefined>();
  const [toDelete, setToDelete] = useState<Wallet | undefined>();
  const [addOpen, setAddOpen] = useState(false);

  const stats = useMemo(() => {
    const balance = wallets.reduce((s, w) => s + w.balanceUsd, 0);
    const spent = wallets.reduce((s, w) => s + w.spentUsd, 0);
    const low = wallets.filter((w) => w.balanceUsd < WALLET_ALERT_THRESHOLD_USD);
    return { balance, spent, low };
  }, [wallets]);

  const onTopup = async (wallet: Wallet, usd: number) => {
    try {
      await topUp.mutateAsync({ walletId: wallet.id, amountUsd: usd });
      toast.success(`Wallet ${wallet.clientName} rechargé: +${formatUSD(usd)}`);
    } catch (err) {
      toast.error(getErrorMessage(err, "Échec du rechargement"));
    }
  };

  const onArchiveConfirm = () => {
    if (!toDelete) return;
    if (authed) {
      // No wallet archive endpoint server-side (deferred). Keep data intact.
      toast.info("L'archivage des portefeuilles n'est pas encore disponible");
    } else {
      archiveMock(toDelete.id);
      toast.success("Wallet archivé");
    }
    setToDelete(undefined);
  };

  return (
    <PageContainer>
      <PageHeader
        title={t("wallets.title")}
        subtitle={`Total ${formatUSD(stats.balance)} · ${stats.low.length} alerte(s)`}
        actions={<Button size="sm" onClick={() => setAddOpen(true)} disabled={wallets.length === 0}><Plus className="h-3 w-3" /> Recharger un wallet</Button>}
      />

      <div className="grid grid-cols-1 md:grid-cols-4 gap-3 mb-5">
        <KPICard label="Solde total (USD)" value={formatUSD(stats.balance)} note={<span>{formatDZD(usdToDzd(stats.balance))}</span>} />
        <KPICard label="Total dépensé (USD)" value={formatUSD(stats.spent)} />
        <KPICard label="Clients avec wallet" value={wallets.length} />
        <KPICard label="Alertes solde faible" value={<span className="text-danger">{stats.low.length}</span>} note={`Seuil < $${WALLET_ALERT_THRESHOLD_USD}`} />
      </div>

      {isError && <ApiFallbackBanner onRetry={() => refetch()} />}

      {stats.low.length > 0 && (
        <div className="flex items-start gap-3 p-4 rounded border border-danger/25 bg-danger-dim mb-4">
          <AlertTriangle className="h-4 w-4 text-danger mt-0.5 shrink-0" />
          <div>
            <h4 className="text-[12.5px] font-bold text-danger mb-0.5">{stats.low.length} wallets en solde faible</h4>
            <p className="text-[11.5px] text-text2">
              {stats.low.map((w) => `${w.clientName} (${formatUSD(w.balanceUsd)})`).join(", ")} — Rechargement requis
            </p>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 xl:grid-cols-[2fr_1fr] gap-3.5">
        <div className="flex flex-col gap-3">
          {isLoading ? (
            <Card><LoadingState rows={4} /></Card>
          ) : wallets.length === 0 ? (
            <Card><EmptyState title="Aucun portefeuille" message="Aucun portefeuille à afficher." /></Card>
          ) : (
            wallets.map((w) => {
              const tone = w.balanceUsd < 0 ? "danger" : w.balanceUsd < WALLET_ALERT_THRESHOLD_USD ? "warning" : "primary";
              const total = w.spentUsd + Math.max(0, w.balanceUsd);
              const pct = total > 0 ? (Math.max(0, w.balanceUsd) / total) * 100 : 0;
              return (
                <Card key={w.id}>
                  <CardBody>
                    <div className="flex items-center justify-between mb-3.5">
                      <div className="flex items-center gap-2.5">
                        <Avatar name={w.clientName} size={36} className="rounded-lg" />
                        <div>
                          <div className="text-[13px] font-bold text-text">{w.clientName}</div>
                          <div className="text-[11px] text-text3">{w.rate} DZD/$</div>
                        </div>
                      </div>
                      <div className="flex items-center gap-1.5">
                        <Badge tone={tone === "danger" ? "danger" : "success"} dot>{tone === "danger" ? "Solde faible" : "Actif"}</Badge>
                        <Button variant="primary" size="xs" onClick={() => setTopupFor(w)}>Recharger</Button>
                        <Button variant="ghost" size="xs" onClick={() => toast.info("Édition à venir")}><Pencil className="h-3 w-3" /></Button>
                        <Button variant="ghost" size="xs" className="text-danger" onClick={() => setToDelete(w)}><Trash2 className="h-3 w-3" /></Button>
                      </div>
                    </div>
                    <div className="flex justify-between text-[11px] mb-1.5">
                      <span className="text-text2">{formatUSD(w.spentUsd)} dépensé</span>
                      <span className={`mono font-bold ${w.balanceUsd < 0 ? "text-danger" : ""}`}>{formatUSD(w.balanceUsd)} restant</span>
                    </div>
                    <ProgressBar value={pct} tone={tone} />
                  </CardBody>
                </Card>
              );
            })
          )}
        </div>

        <Card>
          <CardBody>
            <SectionHeading title="Derniers rechargements" />
            <div className="space-y-1">
              {topups.length === 0 ? (
                <p className="text-[11.5px] text-text3 py-2">Aucun rechargement récent.</p>
              ) : (
                topups.map((t) => (
                  <div key={t.id} className="flex items-start gap-2.5 py-2.5 border-b border-border last:border-0">
                    <div className="flex items-center justify-center h-7 w-7 rounded-lg bg-success-dim text-success shrink-0">
                      <ArrowUpCircle className="h-3.5 w-3.5" />
                    </div>
                    <div className="flex-1">
                      <div className="text-xs font-semibold text-text">{t.clientName}</div>
                      <div className="text-[11px] text-text2">{t.at}</div>
                    </div>
                    <div className="text-right">
                      <div className="mono text-xs font-bold text-success">+{formatUSD(t.amountUsd)}</div>
                      <div className="text-[10px] text-text3">{formatDZD(t.amountDzd)}</div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </CardBody>
        </Card>
      </div>

      <TopupModal wallet={topupFor} onClose={() => setTopupFor(undefined)} onConfirm={(usd) => { if (topupFor) void onTopup(topupFor, usd); setTopupFor(undefined); }} />
      <TopupModal wallet={addOpen ? wallets[0] : undefined} onClose={() => setAddOpen(false)} onConfirm={(usd) => { if (wallets[0]) void onTopup(wallets[0], usd); setAddOpen(false); }} title="Recharger un wallet" />

      <ConfirmDialog
        open={!!toDelete}
        onClose={() => setToDelete(undefined)}
        title="Archiver le wallet"
        message={`Archiver le wallet de ${toDelete?.clientName} ?`}
        confirmLabel="Archiver"
        onConfirm={onArchiveConfirm}
      />
    </PageContainer>
  );
}

function TopupModal({
  wallet,
  onClose,
  onConfirm,
  title = "Recharger le wallet",
}: {
  wallet?: Wallet;
  onClose: () => void;
  onConfirm: (usd: number) => void;
  title?: string;
}) {
  const [usd, setUsd] = useState("");
  const [error, setError] = useState("");
  const value = Number(usd);

  return (
    <Modal
      open={!!wallet}
      onClose={onClose}
      title={title}
      footer={<><Button variant="ghost" size="sm" onClick={onClose}>Annuler</Button><Button size="sm" onClick={() => { if (!value || value <= 0) { setError("Montant invalide"); return; } onConfirm(value); setUsd(""); setError(""); }}>Recharger</Button></>}
    >
      <Field label="Client" required>
        <select className={fieldInputClass} value={wallet?.clientName ?? ""} disabled>
          <option>{wallet?.clientName}</option>
        </select>
      </Field>
      <Field label="Montant USD" required error={error}>
        <input type="number" className={fieldInputClass} value={usd} onChange={(e) => setUsd(e.target.value)} />
      </Field>
      {value > 0 && (
        <p className="text-[11px] text-text2">≈ {formatDZD(usdToDzd(value, DEFAULT_EXCHANGE_RATE))} au taux {DEFAULT_EXCHANGE_RATE} DZD/$</p>
      )}
    </Modal>
  );
}
